import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  TruckIcon,
  SparklesIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { useStorage } from '../hooks/useStorage';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardModern = () => {
  const { customers } = useCustomers();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { users } = useUsers();
  const { getCostOptimizationData } = useStorage();
  const [storageData, setStorageData] = React.useState<any>(null);

  // Load storage data
  React.useEffect(() => {
    const loadStorageData = async () => {
      try {
        const data = await getCostOptimizationData(user?.organizationId);
        setStorageData(data);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    if (user?.organizationId) {
      loadStorageData();
    }
  }, [user?.organizationId, getCostOptimizationData]);

  // Greeting logic
  const currentHour = new Date().getHours();
  let greeting = t('dashboard.welcome.afternoon');
  
  if (currentHour < 12) {
    greeting = t('dashboard.welcome.morning');
  } else if (currentHour < 18) {
    greeting = t('dashboard.welcome.afternoon');
  } else {
    greeting = t('dashboard.welcome.evening');
  }

  // Quick navigation tiles - csak ügyfelek és flottakezelés
  const allQuickNavTiles = [
    {
      name: t('nav.customers'),
      href: '/customers',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      count: customers.length,
      module: 'customers'
    },
    {
      name: t('nav.vehicles'),
      href: '/fleet',
      icon: TruckIcon,
      color: 'from-indigo-500 to-indigo-600',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.bookings'),
      href: '/fleet/bookings',
      icon: ClipboardDocumentListIcon,
      color: 'from-purple-500 to-purple-600',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.customerVehicles'),
      href: '/fleet/customers',
      icon: UserGroupIcon,
      color: 'from-teal-500 to-teal-600',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-gray-600',
      count: null,
      module: 'settings'
    }
  ];

  const quickNavTiles = allQuickNavTiles.filter(tile => {
    return hasAccess(tile.module as keyof import('../types/user').UserModulePermissions);
  });

  // Flotta stats - később implementálható
  const fleetStats = {
    totalVehicles: 0,
    activeBookings: 0,
    maintenanceDue: 0
  };

  // Recent customers
  const recentCustomers = useMemo(() => {
    return [...customers]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [customers]);


  // Customer growth data - last 6 months
  const customerGrowthData = useMemo(() => {
    const now = new Date();
    const monthsData = [];
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthName = monthDate.toLocaleDateString('hu-HU', { month: 'short' });
      
      // Count customers created in this month
      const customersInMonth = customers.filter(c => {
        if (!c.createdAt) return false;
        return c.createdAt >= monthDate && c.createdAt <= monthEnd;
      }).length;
      
      monthsData.push({
        name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        customers: customersInMonth
      });
    }
    
    return monthsData;
  }, [customers]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 100
      }
    }
  };

  // No organization check
  if (!user?.organizationId && user?.role !== 'super_admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 text-blue-500 mb-8">
            <UserGroupIcon className="h-full w-full" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Üdvözöljük, {user?.displayName}!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            A fiókja beállítás alatt van. Kérjük, vegye fel a kapcsolatot a rendszergazdával a hozzáférés aktiválásához.
          </p>
        </div>
      </div>
    );
  }

  // Real system info from hooks
  const totalStorageGB = storageData ? storageData.totalSize / (1024 * 1024 * 1024) : 0;
  const activeUsers = users.filter(u => {
    // Aktív felhasználók: akik az elmúlt 30 napban bejelentkeztek
    if (!u.lastLoginAt) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return u.lastLoginAt >= thirtyDaysAgo;
  }).length;
  
  const systemInfo = {
    storage: {
      used: totalStorageGB,
      total: 100, // Ez lehet egy package limit a jövőben
      unit: 'GB'
    },
    users: {
      active: activeUsers,
      total: users.length
    },
    database: {
      records: customers.length + users.length,
      size: totalStorageGB
    },
    uptime: '99.9%' // Ez egy külső monitoring service-ből jöhetne
  };

  const storagePercentage = (systemInfo.storage.used / systemInfo.storage.total) * 100;

  return (
    <div className="space-y-8 pb-20 sm:pb-0">
        
        {/* System Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20 shadow-lg"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Storage */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 dark:text-gray-400">Tárhely</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.storage.used.toFixed(2)} / {systemInfo.storage.total} {systemInfo.storage.unit}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Active Users */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                <UserGroupIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Aktív felhasználók</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.users.active} / {systemInfo.users.total}
                </div>
              </div>
            </div>

            {/* Database Records */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Adatbázis rekordok</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {systemInfo.database.records.toLocaleString()}
                  {storageData && storageData.totalFiles > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      ({storageData.totalFiles} fájl)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* System Uptime */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Rendszer elérhetőség</div>
                <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                  {systemInfo.uptime}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Hero Header with Glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 p-8 md:p-12 shadow-2xl"
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>
          
          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center gap-4 mb-4"
            >
              <div className="p-4 bg-white/20 backdrop-blur-xl rounded-2xl">
                <ChartBarIcon className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                  {t('dashboard.title')}
                </h1>
                <p className="text-lg md:text-xl text-white/90 flex items-center gap-2">
                  {greeting}, <span className="font-semibold">{user?.displayName || t('dashboard.welcome.user')}</span>! 👋
                </p>
              </div>
            </motion.div>
            
            {/* Quick stats in hero */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
            >
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Ügyfelek</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{customers.length}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Felhasználók</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{users.length}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Aktív felhasználók</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{activeUsers}</div>
              </div>
              <div className="backdrop-blur-xl bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-white/70 text-sm mb-1">Tárhely</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{totalStorageGB.toFixed(2)} GB</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Quick Navigation - Glassmorphism Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="relative overflow-hidden backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-3xl p-6 md:p-8 border border-white/20 dark:border-gray-700/20 shadow-xl"
        >
          {/* Subtle background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5 dark:from-purple-500/10 dark:to-blue-500/10"></div>
          
          <div className="relative z-10">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-3">
                <SparklesIcon className="w-7 h-7 text-purple-600" />
                Gyors navigáció
              </h2>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 md:gap-4">
            {quickNavTiles.map((tile, index) => (
              <motion.div
                key={tile.name}
                variants={itemVariants}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={tile.href}
                  className="group relative overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20 shadow-lg hover:shadow-2xl transition-all duration-300 block h-full"
                >
                  <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                    {/* Icon with gradient */}
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${tile.color} text-white shadow-lg transition-shadow duration-200`}>
                      <tile.icon className="w-6 h-6" />
                    </div>
                    
                    {/* Title */}
                    <div className="space-y-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-xs md:text-sm leading-tight">
                        {tile.name}
                      </h3>
                      {tile.count !== null && (
                        <span className="inline-block px-2.5 py-1 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
                          {tile.count}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
            </div>
          </div>
        </motion.div>

        {/* Customer Growth Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
            <ArrowTrendingUpIcon className="w-6 h-6 text-blue-600" />
            Ügyfél növekedés (utolsó 6 hónap)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={customerGrowthData}>
              <defs>
                <linearGradient id="colorCustomers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} 
              />
              <Area type="monotone" dataKey="customers" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCustomers)" name="Ügyfelek" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Recent Customers */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="relative backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/20 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent flex items-center gap-2">
                <UserGroupIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                {t('dashboard.recentCustomers')}
              </h2>
              <Link 
                to="/customers" 
                className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Összes →
              </Link>
            </div>
            
            <div className="space-y-3">
              {recentCustomers.length > 0 ? (
                recentCustomers.map((customer, index) => (
                  <motion.div
                    key={customer.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    whileHover={{ scale: 1.02, x: 5 }}
                    className="group/item relative overflow-hidden backdrop-blur-sm bg-white/80 dark:bg-gray-700/80 rounded-xl p-4 border border-white/40 dark:border-gray-600/40 shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-indigo-500/0 group-hover/item:from-blue-500/10 group-hover/item:to-indigo-500/10 transition-all duration-300"></div>
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-colors duration-300">
                          {customer.type === 'organization' 
                            ? customer.companyName 
                            : `${customer.lastName} ${customer.firstName}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                          📧 {customer.email}
                        </p>
                      </div>
                      {customer.createdAt && (
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-3 py-1.5 rounded-full group-hover/item:bg-blue-100 dark:group-hover/item:bg-blue-900/40 group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-all duration-300">
                          {format(customer.createdAt, 'MM/dd')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <UserGroupIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('dashboard.noCustomers')}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
    </div>
  );
};

export default DashboardModern;
